import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, computed, EventEmitter, inject, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CheckoutMode, ICalculateTotalsPayload, ICartItem, InvoicingService, ISaleStatus } from '../../../core/services/invoicing.service';
import { saleLinesHaveNonZeroUnitPrice } from '../../../core/services/invoicing-cart.util';
import { MerchantsService } from '../../../core/services/merchants.service';
import { ToastNotificationsService } from '../../../core/services/toast-notifications.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { StoresService } from '../../../core/services/stores.service';
import { Subscription } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CalendarService } from '../../services/calendar.service';
import { LoaderComponent } from '../../../shared-with-client/loader/loader.component';
import { MerchantCurrencyPipe } from '../../../shared/pipes/merchant-currency.pipe';
import { InvoiceItemEditModalComponent } from '../../invoice-item-edit-modal/invoice-item-edit-modal.component';
import { InvoiceDiscountModalComponent } from '../../invoice-discount-modal/invoice-discount-modal.component';
import { EditPaymentsModalComponent } from '../edit-payments-modal/edit-payments-modal.component';
import { Dialog } from '@angular/cdk/dialog';
import { PaymentHistorySummaryComponent } from "../payment-history-summary/payment-history-summary.component";
import { IconComponent } from "../../../shared-with-client/icon/icon.component";
import { ReplaceSaleModalComponent } from '../replace-sale-modal/replace-sale-modal.component';

@Component({
    selector: 'app-invoice-preview',
    imports: [TranslatePipe, LoaderComponent, MerchantCurrencyPipe, DecimalPipe, DatePipe, PaymentHistorySummaryComponent, IconComponent],
    templateUrl: './invoice-preview.component.html',
    styleUrl: './invoice-preview.component.scss',
})
export class InvoicePreviewComponent implements OnInit, OnDestroy {
    @Input() showEditPaymentButton: boolean = false;
    @Input() showSendPaymentLinkButton: boolean = false;
    @Input() fixedSaleData: any;
    @Output() sendPaymentLinkClicked = new EventEmitter<void>();

    CheckoutMode = CheckoutMode;

    private invoicingService = inject(InvoicingService);
    private merchantService = inject(MerchantsService);
    private storesService = inject(StoresService);
    private calendarService = inject(CalendarService);
    private toastNotificationsService = inject(ToastNotificationsService);
    private translate = inject(TranslateService);

    // dialogRef: DialogRef<string> | undefined;

    private bsModalService = inject(BsModalService);
    private modalService = inject(Dialog);

    public currentMerchant = toSignal(this.merchantService.activeMerchant);
    public currentStore = toSignal(this.storesService.getActiveStore());

    public editMode = toSignal(this.invoicingService.editablePreview$, { initialValue: false });

    public loadingTotals = toSignal(this.invoicingService.loadingTotals$, { initialValue: false });

    public checkoutMode = signal<CheckoutMode | null>(null);

    public invoicingPreviewCart = signal<ICalculateTotalsPayload | null>(null);
    public invoicingPreviewPatient = signal<any | null>(null);
    public invoicingPreviewTotals = signal<any | null>({});
    public invoicingPreviewHistory = signal<any | null>(null);

    public creationModeSubscription!: Subscription;
    public invoicingCartSubscription!: Subscription;
    public invoicingPatientSubscription!: Subscription;
    public invoicingTotalsSubscription!: Subscription;
    public existingSalePreviewSubscription!: Subscription;

    public ISaleStatus = ISaleStatus;

    public onlyHasOnlinePayments = computed(() => {
        const payments = this.invoicingPreviewHistory()?.salePayments || [];
        return payments.every(payment => payment.isOnlinePayment);
    });

    public allItemsHaveZeroUnitPrice = computed(() => {
        const saleLines = this.invoicingPreviewCart()?.saleLines;
        return !!saleLines?.length && !saleLinesHaveNonZeroUnitPrice(saleLines);
    });

    public loadingBookingModal = signal(false);

    /**
     * First line with a bookingId (summary date + opening CreateBookingModal).
     * Package prepaid sales store one bookingId per service; POS package cart
     * lines may still share the parent. Opening any package booking loads the
     * full package in the modal.
     */
    public firstLinkedBooking = computed((): { bookingId: number; bookingDateInit: string | Date } | null => {
        const lines = this.invoicingPreviewCart()?.saleLines ?? [];
        const line = lines.find((l: ICartItem) => l.bookingId != null);
        if (!line) {
            return null;
        }
        return { bookingId: line.bookingId, bookingDateInit: line.bookingDateInit };
    });

    ngOnInit(): void {
        // fixed mode (for existing sale details preview)
        if (this.fixedSaleData) {
            this.generatePreviewFromExistingSale(this.fixedSaleData);
            return;
        }

        // Interactive mode (for invoice/sale creation)
        this.creationModeSubscription = this.invoicingService.checkoutMode$.subscribe((mode) => {
            this.checkoutMode.set(mode);
            if (mode === CheckoutMode.CREATE_FULL_SALE) {
                // if sale mode, listen to cart, patient and totals
                this.invoicingCartSubscription = this.invoicingService.invoicingCart$.subscribe((data) => {
                    this.setPreviewCart(data);
                });

                this.invoicingPatientSubscription = this.invoicingService.invoicingPatient$.subscribe((data) => {
                    if (data) {
                        const first = data?.firstName ?? '';
                        const last = data?.lastName ?? '';
                        const clientName = first || last ? `${first} ${last}`.trim() : null;
                        if (clientName) {
                            this.setPreviewPatient({ clientName });
                        }
                    }
                });

                this.invoicingTotalsSubscription = this.invoicingService.invoicingCartTotals$.subscribe((data) => {
                    if (data) {
                        this.setPreviewTotals(data);
                    }
                });

                this.existingSalePreviewSubscription = this.invoicingService.existingSalePreview$.subscribe((data: any) => {
                    if (data?.saleLines?.length) {
                        this.generatePreviewFromExistingSale(data);
                    }
                });
            } else if (mode === CheckoutMode.ADD_PAYMENT) {
                // if payment mode, listen to existing sale preview
                this.existingSalePreviewSubscription = this.invoicingService.existingSalePreview$.subscribe((data: any) => {
                    if (data) {
                        this.generatePreviewFromExistingSale(data);
                    }
                });
            } else if (mode === CheckoutMode.EDIT_SALE || mode === CheckoutMode.INVOICE_REMAINING) {
                // show  existing sale preview
                this.existingSalePreviewSubscription = this.invoicingService.existingSalePreview$.subscribe((data: any) => {
                    if (data) {
                        this.generatePreviewFromExistingSale(data);
                    }
                });

                this.invoicingCartSubscription = this.invoicingService.invoicingCart$.subscribe((data) => {
                    this.setPreviewCart(data);
                });

                this.invoicingTotalsSubscription = this.invoicingService.invoicingCartTotals$.subscribe((data) => {
                    if (data) {
                        this.setPreviewTotals(data);
                    }
                });
            }
        });
    }

    ngOnDestroy(): void {
        if (this.creationModeSubscription) {
            this.creationModeSubscription.unsubscribe();
        }
        if (this.invoicingCartSubscription) {
            this.invoicingCartSubscription.unsubscribe();
        }
        if (this.invoicingPatientSubscription) {
            this.invoicingPatientSubscription.unsubscribe();
        }
        if (this.invoicingTotalsSubscription) {
            this.invoicingTotalsSubscription.unsubscribe();
        }
        if (this.existingSalePreviewSubscription) {
            this.existingSalePreviewSubscription.unsubscribe();
        }
        this.invoicingService.clearExistingSalePreview();
    }

    generatePreviewFromExistingSale(data: any) {
        this.setPreviewCart({ saleLines: data.saleLines, description: data.description });
        if (data.clientName) {
            this.setPreviewPatient({ clientName: data.clientName });
        }
        const totalsData = {
            discountAmountCents: data.discountAmountCents,
            discountPercentage: data.discountPercentage,
            subtotalCents: data.subtotalCents,
            vatCents: data.vatCents,
            totalCents: data.totalCents,
        };
        this.setPreviewTotals(totalsData);
        const historyData = {
            salePayments: data.salePayments,
            status: data.status,
            totalCents: data.totalCents,
            paidAmountCents: data.paidAmountCents,
            creditNotes: data.creditNotes,
        };
        this.setPreviewHistory(historyData);
    }

    setPreviewCart(data: ICalculateTotalsPayload) {
        this.invoicingPreviewCart.set(data);
    }

    setPreviewPatient(data: any) {
        this.invoicingPreviewPatient.set(data);
    }

    setPreviewTotals(data: any) {
        this.invoicingPreviewTotals.set(data);
    }

    setPreviewHistory(data: any) {
        this.invoicingPreviewHistory.set(data);
    }

    removeItemFromCart(cartId: string | undefined) {
        if (!cartId) return;
        this.invoicingService.removeItemFromCart(cartId);
    }

    editPayments() {
        const localPayments = this.invoicingPreviewHistory()?.salePayments?.filter(p => !p.isOnlinePayment) || [];
        const dialog = this.modalService.open<string>(EditPaymentsModalComponent, { data: { payments: localPayments, saleId: this.fixedSaleData?.id } });
        dialog.closed.subscribe((result) => {
            if (result) {
                this.generatePreviewFromExistingSale(result);
            }
        });
    }

    openDiscountModal() {
        this.bsModalService.show(InvoiceDiscountModalComponent, { class: 'modal-md' });
    }

    editItemInCart(item: ICartItem) {
        const initialState = { item };
        this.bsModalService.show(InvoiceItemEditModalComponent, { class: 'modal-md', initialState });
    }

    removeGlobalDiscount() {
        this.invoicingService.invoicingCart$.next({ ...this.invoicingService.invoicingCart$.value, discountAmountCents: 0, discountPercentage: 0 });
    }

    sendPaymentLink() {
        this.sendPaymentLinkClicked.emit();
    }

    hasValidPaymentReference(): boolean {
        if (!this.fixedSaleData?.paymentReference || !this.fixedSaleData?.paymentReferenceExpiresAt) {
            return false;
        }
        const expiresAt = new Date(this.fixedSaleData.paymentReferenceExpiresAt);
        return expiresAt > new Date();
    }

    openLinkedBooking(): void {
        const bookingId = this.firstLinkedBooking()?.bookingId;
        if (!bookingId) {
            return;
        }
        // Get booking
        this.loadingBookingModal.set(true);
        this.calendarService.getBooking(bookingId).subscribe({
            next: (booking) => {
                this.loadingBookingModal.set(false);
                this.calendarService.editBooking(this.currentMerchant(), this.currentStore(), {data: booking});
            }
        });
    }

    replaceSale() {
        this.modalService.open<string>(ReplaceSaleModalComponent).closed.subscribe((result) => {
        });
    }

}
